import React from "react";

import ManageHistoryIcon from "@mui/icons-material/ManageHistory";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import EngineeringProjectForm from "../../components/EngineeringProjectForm.jsx";
import FormattedDate from "../../components/FormattedDate.jsx";
import ProjectStatusChip from "../../components/ProjectStatusChip.jsx";
import ReadableCurrency from "../../components/ReadableCurrency.jsx";
import ReadableProjectName from "../../components/ReadableProjectName.jsx";
import PageContext from "../../contexts/PageContext.jsx";
import EngineeringProjectExpenses from "./EngineeringProjectExpenses.jsx";
import EngineeringProjectSprints from "./EngineeringProjectSprints.jsx";
import EngineeringProjectYearlySummary from "./EngineeringProjectYearlySummary.jsx";

function EngineeringProjectRow({
  // Props
  project,
  mutate,
}) {
  const { editMode } = React.useContext(PageContext);

  //---------------------------------------------------------------------------
  // States
  //---------------------------------------------------------------------------
  const [expanded, setExpanded] = React.useState(false);
  const ref = React.useRef(null);

  //---------------------------------------------------------------------------
  // Callback Functions
  //---------------------------------------------------------------------------
  const handleExpandClick = React.useCallback(() => {
    setExpanded((previous) => !previous);
  }, [setExpanded]);

  React.useEffect(() => {
    if (expanded) {
      setTimeout(() => {
        const y = ref.current?.getBoundingClientRect().top;
        if (y) {
          window.scrollTo({ top: y + window.scrollY - 70, behavior: "smooth" });
        }
      }, 300);
    }
  }, [expanded]);

  const projectSprints = React.useMemo(() => {
    const sprints = new Map();
    project.projectWork?.forEach((work) => {
      const sprint = sprints.get(work.sprint.name);
      if (!sprint) {
        sprints.set(work.sprint.name, { ...work.sprint, hours: work.hours, cost: work.cost });
      } else {
        sprints.set(work.sprint.name, {
          ...sprint,
          hours: sprint.hours + work.hours,
          cost: sprint.cost + work.cost,
        });
      }
    });
    return Array.from(sprints, ([key, value]) => value);
  }, [project.projectWork]);

  return (
    <Card square ref={ref} sx={{ my: 0.5, ...(expanded && { my: 1, boxShadow: 6 }) }}>
      <Grid
        container
        spacing={2}
        onClick={handleExpandClick}
        sx={{
          alignItems: "center",
          p: 1,
          cursor: "pointer",
          minHeight: "3.5rem",
          ...(expanded && { fontWeight: "bold" }),
        }}
      >
        <Grid sx={{ display: { xs: "none", sm: "block" } }} size="auto">
          {editMode ? (
            <EngineeringProjectForm project={project} mutate={mutate} />
          ) : (
            <ManageHistoryIcon sx={{ m: 1 }} />
          )}
        </Grid>
        <Grid size={{ xs: 4, sm: 2.5, md: 1.5 }}>
          <ProjectStatusChip status={project.status} />
        </Grid>
        <Grid size="grow" sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ fontWeight: "inherit", ml: 1 }}>
            <ReadableProjectName name={project.name} jiraId={project.jiraId} />
          </Typography>
        </Grid>
        <Grid size={1.5} sx={{ display: { xs: "none", md: "block" } }}>
          <Typography sx={{ fontWeight: "inherit" }}>{project.gl}</Typography>
        </Grid>
        <Grid size={{ xs: 1.75, md: 1.5, lg: 1.25 }} sx={{ display: { xs: "none", sm: "block" } }}>
          <ReadableCurrency value={project.budget} />
        </Grid>
        <Grid size={{ xs: 3, sm: 1.5, lg: 1.25 }}>
          <ReadableCurrency value={project.cost || 0} />
        </Grid>
        <Grid size={1.5} sx={{ display: { xs: "none", md: "block" } }}>
          <Typography sx={{ fontWeight: "inherit" }}>
            <FormattedDate dateString={project.startDate} />
          </Typography>
        </Grid>
        <Grid size={{ xs: 2, md: 1.5 }} sx={{ display: { xs: "none", sm: "block" } }}>
          <Typography sx={{ fontWeight: "inherit" }}>
            <FormattedDate dateString={project.endDate} />
          </Typography>
        </Grid>
      </Grid>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider variant="middle" sx={{ my: 1, mx: -1 }} />
        <Box sx={{ m: 2 }}>
          <EngineeringProjectYearlySummary project={project} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <EngineeringProjectExpenses project={project} mutate={mutate} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <EngineeringProjectSprints project={project} sprints={projectSprints} mutate={mutate} />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Card>
  );
}

export default EngineeringProjectRow;
